import React from 'react';
import AuthContext from '../auth/AuthContext';
import NotificationCard from './NotificationCard.jsx';
import { loadNotificationParams } from '../auth/tokenUtils';

export default function NotificationApp() {
  const { user, accessToken, isBootstrapping, isAuthenticated, isTokenInvalid } = React.useContext(AuthContext);
  const [htmlContent, setHtmlContent] = React.useState(null);
  const [imgBlobURL, setImgBlobURL] = React.useState('');
  const [trainingName, setTrainingName] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  // Read trainingId and llId from URL query params; fall back to localStorage
  const _qp = new URLSearchParams(window.location.search);
  const _stored = loadNotificationParams();
  const trainingId = _qp.get('trainingId') || _stored.trainingId || '';
  const llId = _qp.get('llId') || _stored.llId || '';

  const tokenReady = !isBootstrapping && isAuthenticated && !isTokenInvalid;

  React.useEffect(() => {
    if (!tokenReady) return;

    if (!trainingId || !llId) {
      setError('Missing trainingId or llId in URL parameters.');
      setLoading(false);
      return;
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    if (!baseUrl) {
      setError('API base URL is not configured. Check REACT_APP_API_BASE_URL in your .env file.');
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const url = `${baseUrl.replace(/\/+$/, '')}/api/Training/gettrainingcontentdetails`;

    fetch(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ llId, trainingId: Number(trainingId), language: 'EN-US' }),
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (data.isSuccess === '1') {
          setHtmlContent(data.content || '');
          const raw = typeof (data.imgBlobURL ?? data.imgBlobUrl) === 'string' ? (data.imgBlobURL ?? data.imgBlobUrl).trim() : '';
          let candidate = raw;
          if (raw && !/^(https?:|blob:|data:)/i.test(raw)) {
            const ext = (typeof data.blobFileName === 'string' && data.blobFileName.includes('.'))
              ? data.blobFileName.split('.').pop().toLowerCase()
              : 'png';
            const mime = ext === 'svg' ? 'image/svg+xml' : `image/${ext}`;
            candidate = `data:${mime};base64,${raw}`;
          }
          setImgBlobURL(candidate);
          setTrainingName(typeof data.trainingName === 'string' ? data.trainingName.trim() : '');
        } else if (!data.content && !data.trainingName) {
          setHtmlContent('');
          setError(data.errorMessage || 'No data found.');
          setImgBlobURL('');
          setTrainingName('');
        } else {
          setHtmlContent('');
          setError(data.errorMessage || 'Failed to load notification.');
          setImgBlobURL('');
          setTrainingName('');
        }
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setHtmlContent('');
          setError(err.message);
          setImgBlobURL('');
          setTrainingName('');
        }
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [tokenReady, trainingId, llId, accessToken]);

  if (isBootstrapping) return <div style={{ textAlign: 'left' }}>Loading…</div>;
  if (isTokenInvalid || !isAuthenticated) {
    return <div style={{ color: 'red', textAlign: 'left' }}>Invalid or missing access token. Please provide a valid token in the URL.</div>;
  }
  if (loading) return <div style={{ textAlign: 'left' }}>Loading…</div>;
  if (error) return <div id="noContentFound" style={{ color: 'red', textAlign: 'left' }}>{error}</div>;
  if (htmlContent === null) return null;

  return (
    <NotificationCard
      name={user?.name || 'Amit Maurya'}
      firstName={user?.name?.split(' ')[0] || 'Amit'}
      lastName={user?.name?.split(' ')[1] || ''}
      trainingName={trainingName}
      avatarUrl={imgBlobURL}
      htmlContent={htmlContent}
    />
  );
}
